import { useCallback, useEffect, useMemo, useState } from 'react';
import { Copy, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { createQuickCode, listQuickCodes } from '@/api/member';
import type { MemberTypeConfig, QuickCodeItem } from '@shared/api.interface';
import { QUICK_CODE_SOURCE_CHANNEL, QUICK_CODE_WECHAT_MAX_LENGTH } from '@shared/api.interface';
import { isValidOrderNo } from '@shared/input-validation';
import { extractApiErrorMessage } from './api-error';
import { buildQuickCodeMessage, copyText, quickCodeStatus } from './quick-code-message';

interface QuickCodePanelProps {
  memberTypeConfigs: MemberTypeConfig[];
  isLoadingTypes: boolean;
}

const formatTime = (value: string) =>
  value ? new Date(value).toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';

export function QuickCodePanel({ memberTypeConfigs, isLoadingTypes }: QuickCodePanelProps) {
  // 只列出“年度总次数”类型：会员类型里新建的次卡会自动出现
  const cardTypes = useMemo(() => memberTypeConfigs.filter(config => config.quota_mode === 'total'), [memberTypeConfigs]);
  const [selectedType, setSelectedType] = useState('');
  const [wechat, setWechat] = useState('');
  const [orderNo, setOrderNo] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [latest, setLatest] = useState<QuickCodeItem | null>(null);
  const [recent, setRecent] = useState<QuickCodeItem[]>([]);
  const [isLoadingRecent, setIsLoadingRecent] = useState(false);
  const host = typeof window !== 'undefined' ? window.location.host : '';

  useEffect(() => {
    if (!cardTypes.some(config => config.name === selectedType) && cardTypes.length) {
      setSelectedType(cardTypes[0].name);
    }
  }, [cardTypes, selectedType]);

  const fetchRecent = useCallback(async () => {
    setIsLoadingRecent(true);
    try {
      setRecent((await listQuickCodes()).items);
    } catch {
      toast.error('获取最近发出的码失败');
    } finally {
      setIsLoadingRecent(false);
    }
  }, []);

  useEffect(() => {
    void fetchRecent();
  }, [fetchRecent]);

  const orderNoError = orderNo.trim() && !isValidOrderNo(orderNo.trim()) ? '订单号只能包含字母、数字、下划线或连字符' : '';

  const handleCreate = async () => {
    if (!selectedType || orderNoError || isCreating) return;
    setIsCreating(true);
    try {
      const item = await createQuickCode({
        member_type: selectedType,
        wechat: wechat.trim() || undefined,
        order_no: orderNo.trim() || undefined,
      });
      setLatest(item);
      setWechat('');
      setOrderNo('');
      toast.success('已生成，记得复制发给客户');
      void fetchRecent();
    } catch (error) {
      toast.error(extractApiErrorMessage(error, '生成失败，请重试'));
    } finally {
      setIsCreating(false);
    }
  };

  const copy = async (text: string, success: string) => {
    if (await copyText(text)) toast.success(success);
    else toast.error('浏览器不允许自动复制，请长按选中后复制');
  };

  const generateCard = (
    <section aria-labelledby="quick-code-title" className="min-w-0 rounded-2xl bg-white p-5 shadow-[0_0_0_1px_var(--border)]">
      <h2 id="quick-code-title" className="mb-3 text-lg font-semibold">给客户发一个次卡码</h2>
      <span className="mb-2 block text-sm font-medium">次卡类型</span>
      {isLoadingTypes && !cardTypes.length ? (
        <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
      ) : !cardTypes.length ? (
        <p className="rounded-xl bg-amber-50 px-3 py-3 text-sm text-amber-800">
          还没有次卡类型。请先到“会员类型 → 年度总次数”新建一个，例如总次数为 3 的“三次卡”。
        </p>
      ) : (
        <div className="grid grid-cols-[repeat(auto-fit,minmax(130px,1fr))] gap-2.5" role="radiogroup" aria-label="次卡类型">
          {cardTypes.map(config => {
            const active = config.name === selectedType;
            return (
              <button
                key={config.id}
                type="button"
                role="radio"
                aria-checked={active}
                onClick={() => setSelectedType(config.name)}
                className={`rounded-xl border-[1.5px] px-3.5 py-3 text-left transition-colors duration-150 ease-out ${active ? 'border-cta bg-orange-50' : 'border-border bg-white hover:bg-accent'}`}
              >
                <span className={`block text-base font-semibold ${active ? 'text-cta' : ''}`}>{config.name}</span>
                <span className="text-xs text-muted-foreground">可领 {config.total_quota} 个 · {config.validity_months} 个月</span>
              </button>
            );
          })}
        </div>
      )}

      <div className="mt-4 space-y-1.5">
        <label htmlFor="quick-wechat" className="text-sm font-medium">客户微信号（选填）</label>
        <Input
          id="quick-wechat"
          className="h-11"
          maxLength={QUICK_CODE_WECHAT_MAX_LENGTH}
          autoComplete="off"
          placeholder="例如：lily_mom"
          value={wechat}
          onChange={(e) => setWechat(e.target.value)}
        />
      </div>
      <div className="mt-4 space-y-1.5">
        <label htmlFor="quick-order" className="text-sm font-medium">订单号（选填）</label>
        <Input
          id="quick-order"
          className="h-11 font-mono"
          maxLength={64}
          autoComplete="off"
          placeholder="微信转账可不填"
          value={orderNo}
          aria-invalid={Boolean(orderNoError)}
          onChange={(e) => setOrderNo(e.target.value)}
        />
        {orderNoError && <p className="text-xs text-destructive">{orderNoError}</p>}
      </div>
      <Button
        onClick={handleCreate}
        disabled={!selectedType || Boolean(orderNoError) || isCreating}
        className="mt-4 h-12 w-full rounded-xl bg-cta text-base font-semibold text-cta-foreground hover:bg-cta-hover"
      >
        {isCreating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        生成兑换码
      </Button>

      {latest && (
        <div className="mt-4 space-y-3 rounded-xl bg-orange-50 p-4" aria-live="polite">
          <p className="break-all text-center font-mono text-3xl font-bold tracking-[0.12em] text-orange-900">{latest.passcode}</p>
          <p className="whitespace-pre-wrap rounded-lg border border-dashed bg-white px-3 py-2.5 text-sm">{buildQuickCodeMessage(latest, host)}</p>
          <div className="flex flex-wrap gap-2">
            <Button
              className="h-11 flex-1 bg-cta font-semibold text-cta-foreground hover:bg-cta-hover"
              onClick={() => void copy(buildQuickCodeMessage(latest, host), '话术已复制，去微信粘贴发送')}
            >
              <Copy className="mr-1 h-4 w-4" />
              复制话术
            </Button>
            <Button variant="outline" className="h-11 flex-1 bg-white" onClick={() => void copy(latest.passcode, '兑换码已复制')}>
              只复制码
            </Button>
          </div>
        </div>
      )}
      <p className="mt-3 text-xs text-muted-foreground">只列出“年度总次数”类型；年卡请到“会员管理 → 新增会员”。码在客户首次使用时才开始计算有效期。</p>
    </section>
  );

  const recentCard = (
    <section aria-labelledby="quick-recent-title" className="min-w-0 rounded-2xl bg-white p-5 shadow-[0_0_0_1px_var(--border)]">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h2 id="quick-recent-title" className="text-lg font-semibold">最近发出的码</h2>
        <Button variant="ghost" size="sm" onClick={() => void fetchRecent()} disabled={isLoadingRecent}>
          <RefreshCw className={`mr-1 h-4 w-4 ${isLoadingRecent ? 'animate-spin' : ''}`} />
          刷新
        </Button>
      </div>
      {!recent.length ? (
        <p className="py-6 text-center text-sm text-muted-foreground">
          {isLoadingRecent ? '加载中…' : '还没有快捷发过码。生成的码会显示在这里，方便再次复制。'}
        </p>
      ) : (
        <ul className="divide-y">
          {recent.map(item => {
            const status = quickCodeStatus(item);
            return (
              <li key={item.passcode} className="flex items-center gap-2.5 py-2.5">
                <div className="min-w-0 flex-1">
                  <p className="font-mono font-semibold">{item.passcode}</p>
                  <p className="truncate text-xs text-muted-foreground">
                    {item.member_type} · {item.wechat ? `微信：${item.wechat}` : '未填微信号'}{item.order_no ? ` · ${item.order_no}` : ''} · {formatTime(item.created_at)}
                  </p>
                </div>
                <span className={`shrink-0 rounded-full px-2 text-xs font-medium ${status.className}`}>{status.label}</span>
                <Button
                  size="sm"
                  variant="outline"
                  className="shrink-0"
                  onClick={() => void copy(buildQuickCodeMessage(item, host), '话术已复制')}
                >
                  复制话术
                </Button>
              </li>
            );
          })}
        </ul>
      )}
      <p className="mt-3 text-xs text-muted-foreground">
        来源渠道自动记为“{QUICK_CODE_SOURCE_CHANNEL}”，在会员管理里可按码、微信号或订单号搜到，也能退款作废。
      </p>
    </section>
  );

  return (
    <div className="grid items-start gap-4 lg:grid-cols-2">
      {generateCard}
      {recentCard}
    </div>
  );
}
